/**
 * PhysiologicalEffectsCatalogPage — v10.6.0 Fase 2
 * Catálogo global de definiciones de efectos fisiológicos.
 * Permite crear, editar, activar/desactivar efectos reutilizables.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { physiologicalEffectsAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import {
  Plus, Pencil, Trash, Pulse, MagnifyingGlass, Spinner,
  WarningCircle, ArrowRight, Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TARGET_VARIABLES = [
  { value: 'heart_rate', label: 'Frecuencia cardíaca', unit: 'lpm', numeric: true },
  { value: 'systolic_bp', label: 'Presión sistólica', unit: 'mmHg', numeric: true },
  { value: 'diastolic_bp', label: 'Presión diastólica', unit: 'mmHg', numeric: true },
  { value: 'mean_bp', label: 'Presión arterial media', unit: 'mmHg', numeric: true },
  { value: 'spo2', label: 'Saturación O₂', unit: '%', numeric: true },
  { value: 'respiratory_rate', label: 'Frecuencia respiratoria', unit: 'rpm', numeric: true },
  { value: 'temperature', label: 'Temperatura', unit: '°C', numeric: true },
  { value: 'consciousness', label: 'Conciencia (GCS)', unit: 'GCS', numeric: true },
  { value: 'pain_score', label: 'Escala de dolor', unit: '', numeric: true },
  { value: 'end_tidal_co2', label: 'ETCO₂', unit: 'mmHg', numeric: true },
  { value: 'ecg.rhythm', label: 'Ritmo ECG', unit: '', numeric: false },
];

const OPERATORS = [
  { value: 'SET', label: 'Establecer' },
  { value: 'ADD', label: 'Sumar' },
  { value: 'SUBTRACT', label: 'Restar' },
  { value: 'MULTIPLY', label: 'Multiplicar' },
  { value: 'PERCENTAGE', label: 'Porcentaje' },
  { value: 'TARGET', label: 'Objetivo' },
];

const STACKING = ['stack', 'replace', 'max', 'min', 'latest', 'none'];

const LABELS = {
  beneficial: 'bg-green-100 text-green-700 border-green-300',
  neutral: 'bg-slate-100 text-slate-700 border-slate-300',
  adverse: 'bg-orange-100 text-orange-700 border-orange-300',
  critical: 'bg-red-100 text-red-700 border-red-300',
};

const RHYTHMS = [
  'sinusal', 'taquicardia_sinusal', 'bradicardia_sinusal',
  'fibrilacion_auricular', 'fibrilacion_ventricular',
  'taquicardia_ventricular', 'asistolia', 'aesp',
  'bloqueo_av_completo', 'flutter_auricular',
];

const defaultEffect = {
  name: '',
  description: '',
  target_variable: 'heart_rate',
  operator: 'SET',
  value: 100,
  onset_delay_seconds: 0,
  transition_duration_seconds: 5,
  effect_duration_seconds: null,
  priority: 500,
  stacking_mode: 'stack',
  repeatable: true,
  label: 'neutral',
  active: true,
};

export default function PhysiologicalEffectsCatalogPage() {
  const { isSuperAdmin, hasPermission } = useAuth();
  const [effects, setEffects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(defaultEffect);
  const [saving, setSaving] = useState(false);

  const canManage = isSuperAdmin || hasPermission('medicamentos', 'crear');

  const load = async () => {
    setLoading(true);
    try {
      const res = await physiologicalEffectsAPI.list();
      setEffects(res.data?.effects || []);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar efectos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openModal = (e = null) => {
    if (e) {
      setEditing(e);
      setFormData({ ...defaultEffect, ...e });
    } else {
      setEditing(null);
      setFormData(defaultEffect);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nombre obligatorio');
      return;
    }
    setSaving(true);
    try {
      const varDef = TARGET_VARIABLES.find((v) => v.value === formData.target_variable);
      const payload = {
        ...formData,
        value: varDef?.numeric ? Number(formData.value) : formData.value,
        effect_duration_seconds:
          formData.effect_duration_seconds === '' || formData.effect_duration_seconds === null
            ? null : Number(formData.effect_duration_seconds),
      };
      if (editing) {
        await physiologicalEffectsAPI.update(editing.id, payload);
        toast.success('Efecto actualizado');
      } else {
        await physiologicalEffectsAPI.create(payload);
        toast.success('Efecto creado');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e) => {
    if (!window.confirm(`¿Desactivar el efecto "${e.name}"?`)) return;
    try {
      await physiologicalEffectsAPI.delete(e.id);
      toast.success('Efecto desactivado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const filtered = effects.filter((e) => {
    const term = (searchTerm || '').toLowerCase();
    const nameMatch = (e.name || '').toLowerCase().includes(term)
      || (e.target_variable || '').toLowerCase().includes(term);
    const labelMatch = selectedLabel === 'all' || e.label === selectedLabel;
    return nameMatch && labelMatch;
  });

  const varLabel = (v) => TARGET_VARIABLES.find((x) => x.value === v)?.label || v;
  const varUnit = (v) => TARGET_VARIABLES.find((x) => x.value === v)?.unit || '';
  const opSymbol = (op) => ({
    SET: '=', ADD: '+', SUBTRACT: '−', MULTIPLY: '×', PERCENTAGE: '%', TARGET: '→',
  }[op] || op);

  const currentVarDef = TARGET_VARIABLES.find((v) => v.value === formData.target_variable);
  const isRhythm = formData.target_variable === 'ecg.rhythm';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={48} className="animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900 flex items-center gap-2" data-testid="effects-catalog-title">
            <Pulse size={28} className="text-purple-600" />
            Efectos Fisiológicos
          </h1>
          <p className="text-slate-500 text-sm">
            {effects.length} efecto{effects.length === 1 ? '' : 's'} en el catálogo. Reutilizables en medicamentos y acciones clínicas.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => openModal()} className="gap-2 bg-purple-600 hover:bg-purple-700" data-testid="add-effect-btn">
            <Plus size={18} />
            Nuevo efecto
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o variable…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={selectedLabel} onValueChange={setSelectedLabel}>
              <SelectTrigger className="w-full md:w-52" data-testid="label-filter">
                <SelectValue placeholder="Etiqueta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las etiquetas</SelectItem>
                <SelectItem value="beneficial">Beneficioso</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="adverse">Adverso</SelectItem>
                <SelectItem value="critical">Crítico</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <WarningCircle size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">Sin efectos en el catálogo</p>
          {canManage && (
            <Button onClick={() => openModal()} className="mt-4 gap-2 bg-purple-600 hover:bg-purple-700">
              <Plus size={16} />
              Crear el primero
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((e) => (
            <Card key={e.id} className="hover:shadow-md transition-shadow" data-testid={`effect-card-${e.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base truncate">{e.name}</CardTitle>
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${LABELS[e.label] || 'bg-slate-100'}`}>
                        {e.label}
                      </Badge>
                      {e.active === false && <Badge className="bg-slate-100 text-slate-600 text-[10px]">inactivo</Badge>}
                      <Badge variant="secondary" className="text-[10px]">v{e.version || 1}</Badge>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openModal(e)} data-testid={`edit-effect-${e.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(e)}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="p-2 bg-purple-50 rounded-md border border-purple-100">
                  <div className="text-[10px] text-purple-600 font-semibold uppercase tracking-wide">Efecto</div>
                  <div className="font-mono text-purple-900 text-sm">
                    <span className="font-semibold">{varLabel(e.target_variable)}</span>
                    {' '}
                    <span className="font-bold text-purple-700">{opSymbol(e.operator)}</span>
                    {' '}
                    <span>{String(e.value)}</span>
                    {' '}
                    <span className="text-purple-600">{varUnit(e.target_variable)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {e.transition_duration_seconds > 0 && (
                    <span className="flex items-center gap-1">
                      <ArrowRight size={12} /> {e.transition_duration_seconds}s
                    </span>
                  )}
                  {e.effect_duration_seconds && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> dura {e.effect_duration_seconds}s
                    </span>
                  )}
                  {e.onset_delay_seconds > 0 && (
                    <span>inicio +{e.onset_delay_seconds}s</span>
                  )}
                  <span className="text-slate-400">prio {e.priority}</span>
                </div>
                {e.description && (
                  <p className="text-xs text-slate-600 line-clamp-2">{e.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pulse size={22} className="text-purple-600" />
              {editing ? 'Editar efecto' : 'Nuevo efecto fisiológico'}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-4">
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Taquicardia por adrenalina"
                    data-testid="effect-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Etiqueta</Label>
                  <Select value={formData.label} onValueChange={(v) => setFormData({ ...formData, label: v })}>
                    <SelectTrigger data-testid="effect-label-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beneficial">Beneficioso</SelectItem>
                      <SelectItem value="neutral">Neutral</SelectItem>
                      <SelectItem value="adverse">Adverso</SelectItem>
                      <SelectItem value="critical">Crítico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Variable objetivo *</Label>
                  <Select
                    value={formData.target_variable}
                    onValueChange={(v) => setFormData({
                      ...formData,
                      target_variable: v,
                      value: v === 'ecg.rhythm' ? 'sinusal' : 100,
                      operator: v === 'ecg.rhythm' ? 'SET' : formData.operator,
                    })}
                  >
                    <SelectTrigger data-testid="effect-var-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TARGET_VARIABLES.map((v) => (
                        <SelectItem key={v.value} value={v.value}>{v.label}{v.unit ? ` (${v.unit})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Operador</Label>
                  <Select
                    value={formData.operator}
                    onValueChange={(v) => setFormData({ ...formData, operator: v })}
                    disabled={isRhythm}
                  >
                    <SelectTrigger data-testid="effect-op-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OPERATORS.map((o) => (
                        <SelectItem key={o.value} value={o.value} disabled={isRhythm && o.value !== 'SET'}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Valor *</Label>
                  {isRhythm ? (
                    <Select value={String(formData.value)} onValueChange={(v) => setFormData({ ...formData, value: v })}>
                      <SelectTrigger data-testid="effect-value-rhythm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RHYTHMS.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      data-testid="effect-value-input"
                    />
                  )}
                </div>
              </div>

              {/* Preview */}
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                <div className="text-[11px] text-purple-700 font-semibold mb-1">Vista previa del cambio</div>
                <div className="text-sm font-mono text-purple-900">
                  {varLabel(formData.target_variable)}
                  {' '}
                  <span className="font-bold">{opSymbol(formData.operator)}</span>
                  {' '}
                  {String(formData.value)} {currentVarDef?.unit || ''}
                  {formData.transition_duration_seconds > 0 && (
                    <span className="text-purple-600"> · transición {formData.transition_duration_seconds}s</span>
                  )}
                  {formData.effect_duration_seconds && (
                    <span className="text-purple-600"> · dura {formData.effect_duration_seconds}s</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Retraso inicio (s)</Label>
                  <Input
                    type="number" min="0"
                    value={formData.onset_delay_seconds}
                    onChange={(e) => setFormData({ ...formData, onset_delay_seconds: parseInt(e.target.value || 0) })}
                    data-testid="effect-onset-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Transición (s)</Label>
                  <Input
                    type="number" min="0"
                    value={formData.transition_duration_seconds}
                    onChange={(e) => setFormData({ ...formData, transition_duration_seconds: parseInt(e.target.value || 0) })}
                    data-testid="effect-transition-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Duración (s)</Label>
                  <Input
                    type="number" min="0"
                    value={formData.effect_duration_seconds ?? ''}
                    onChange={(e) => setFormData({ ...formData, effect_duration_seconds: e.target.value === '' ? null : parseInt(e.target.value) })}
                    placeholder="permanente"
                    data-testid="effect-duration-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Modo combinación</Label>
                  <Select value={formData.stacking_mode} onValueChange={(v) => setFormData({ ...formData, stacking_mode: v })}>
                    <SelectTrigger data-testid="effect-stacking-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STACKING.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Prioridad (0-1000)</Label>
                  <Input
                    type="number" min="0" max="1000"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value || 500) })}
                    data-testid="effect-priority-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  data-testid="effect-description-input"
                />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-effect-btn">
              {saving ? <Spinner size={18} className="animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
